import { useState } from 'react';
import { Title, useNotify } from 'react-admin';
import { useQuery, useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import RefreshIcon from '@mui/icons-material/Refresh';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';

import {
  GET_CURRENCIES,
  CREATE_CURRENCY,
  UPDATE_CURRENCY,
  SET_DEFAULT_CURRENCY,
} from './subscription.queries';
import { AdminCurrency, formatDate } from './subscription.types';

// ============================================================================
// CURRENCY TABLE
// ============================================================================

interface CurrencyTableProps {
  currencies: AdminCurrency[];
  onEdit: (currency: AdminCurrency) => void;
  onSetDefault: (code: string) => void;
}

function CurrencyTable({ currencies, onEdit, onSetDefault }: CurrencyTableProps) {
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Nom</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Symbole</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Taux de change</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">Décimales</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">Statut</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">Par défaut</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Dernière MAJ</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currencies.map((currency) => (
            <TableRow
              key={currency.id}
              hover
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                bgcolor: currency.isDefault ? 'primary.50' : 'inherit',
              }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                  {currency.code}
                </Typography>
              </TableCell>
              <TableCell>{currency.name}</TableCell>
              <TableCell>
                <Typography variant="body2" fontFamily="monospace" fontSize="1.1em">
                  {currency.symbol}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                  <Typography variant="body2" fontFamily="monospace">
                    {currency.exchangeRate.toFixed(4)}
                  </Typography>
                  {currency.manualRate && (
                    <Tooltip title={`Taux manuel: ${currency.manualRate.toFixed(4)}`}>
                      <Chip label="Manuel" size="small" color="warning" variant="outlined" />
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
              <TableCell align="center">{currency.decimals}</TableCell>
              <TableCell align="center">
                <Chip
                  label={currency.isActive ? 'Actif' : 'Inactif'}
                  size="small"
                  color={currency.isActive ? 'success' : 'default'}
                  variant={currency.isActive ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell align="center">
                {currency.isDefault ? (
                  <Chip
                    icon={<StarIcon />}
                    label="Par défaut"
                    size="small"
                    color="primary"
                  />
                ) : (
                  <Tooltip title="Définir comme devise par défaut">
                    <IconButton
                      size="small"
                      onClick={() => onSetDefault(currency.code)}
                      disabled={!currency.isActive}
                    >
                      <StarBorderIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {currency.lastRateUpdate
                    ? formatDate(currency.lastRateUpdate)
                    : '-'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Modifier">
                  <IconButton size="small" onClick={() => onEdit(currency)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ============================================================================
// CREATE/EDIT CURRENCY DIALOG
// ============================================================================

interface CurrencyDialogProps {
  open: boolean;
  currency: AdminCurrency | null;
  onClose: () => void;
  onSave: (data: CurrencyFormData) => void;
  loading: boolean;
}

interface CurrencyFormData {
  code: string;
  name: string;
  symbol: string;
  decimals: number;
  exchangeRate: number;
  isActive: boolean;
}

function CurrencyDialog({ open, currency, onClose, onSave, loading }: CurrencyDialogProps) {
  const isEdit = !!currency;
  const [formData, setFormData] = useState<CurrencyFormData>({
    code: currency?.code || '',
    name: currency?.name || '',
    symbol: currency?.symbol || '',
    decimals: currency?.decimals ?? 0,
    exchangeRate: currency?.exchangeRate ?? 1,
    isActive: currency?.isActive ?? true,
  });

  const handleChange = (field: keyof CurrencyFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: field === 'decimals' || field === 'exchangeRate' ? Number(value) : value,
    }));
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  // Reset form when dialog opens with new currency
  useState(() => {
    if (open) {
      setFormData({
        code: currency?.code || '',
        name: currency?.name || '',
        symbol: currency?.symbol || '',
        decimals: currency?.decimals ?? 0,
        exchangeRate: currency?.exchangeRate ?? 1,
        isActive: currency?.isActive ?? true,
      });
    }
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? `Modifier ${currency.code}` : 'Nouvelle devise'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Code ISO"
            value={formData.code}
            onChange={handleChange('code')}
            disabled={isEdit}
            required
            placeholder="EUR, USD, XOF..."
            helperText="Code ISO 4217 de la devise (ex: EUR, USD, XOF)"
            inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
          />
          <TextField
            label="Nom"
            value={formData.name}
            onChange={handleChange('name')}
            required
            placeholder="Euro, Dollar US, Franc CFA..."
          />
          <TextField
            label="Symbole"
            value={formData.symbol}
            onChange={handleChange('symbol')}
            required
            placeholder="€, $, FCFA..."
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Taux de change"
              type="number"
              value={formData.exchangeRate}
              onChange={handleChange('exchangeRate')}
              required
              inputProps={{ step: 0.0001, min: 0 }}
              helperText="Par rapport à la devise de base (XOF)"
              fullWidth
            />
            <TextField
              label="Décimales"
              type="number"
              value={formData.decimals}
              onChange={handleChange('decimals')}
              inputProps={{ min: 0, max: 4 }}
              sx={{ width: 150 }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleChange('isActive')}
              />
            }
            label="Devise active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.code || !formData.name || !formData.symbol}
        >
          {loading ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function CurrencyList() {
  const notify = useNotify();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<AdminCurrency | null>(null);

  // Queries
  const { data, loading, error, refetch } = useQuery(GET_CURRENCIES);

  // Mutations
  const [createCurrency, { loading: creating }] = useMutation(CREATE_CURRENCY);
  const [updateCurrency, { loading: updating }] = useMutation(UPDATE_CURRENCY);
  const [setDefaultCurrency, { loading: settingDefault }] = useMutation(SET_DEFAULT_CURRENCY);

  const currencies: AdminCurrency[] = data?.adminCurrencies || [];
  const activeCurrencies = currencies.filter((c) => c.isActive).length;
  const defaultCurrency = currencies.find((c) => c.isDefault);

  // Handlers
  const handleCreate = () => {
    setSelectedCurrency(null);
    setDialogOpen(true);
  };

  const handleEdit = (currency: AdminCurrency) => {
    setSelectedCurrency(currency);
    setDialogOpen(true);
  };

  const handleSave = async (formData: CurrencyFormData) => {
    try {
      if (selectedCurrency) {
        // Update
        await updateCurrency({
          variables: {
            id: selectedCurrency.id,
            name: formData.name,
            symbol: formData.symbol,
            exchangeRate: formData.exchangeRate,
            isActive: formData.isActive,
          },
        });
        notify('Devise mise à jour', { type: 'success' });
      } else {
        // Create
        await createCurrency({
          variables: {
            code: formData.code.toUpperCase(),
            name: formData.name,
            symbol: formData.symbol,
            decimals: formData.decimals,
            exchangeRate: formData.exchangeRate,
            isActive: formData.isActive,
          },
        });
        notify('Devise créée', { type: 'success' });
      }
      setDialogOpen(false);
      refetch();
    } catch (err) {
      notify('Erreur lors de l\'enregistrement', { type: 'error' });
      console.error(err);
    }
  };

  const handleSetDefault = async (code: string) => {
    try {
      await setDefaultCurrency({ variables: { code } });
      notify(`${code} défini comme devise par défaut`, { type: 'success' });
      refetch();
    } catch (err) {
      notify('Erreur lors du changement de devise par défaut', { type: 'error' });
      console.error(err);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box p={3}>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Erreur lors du chargement des devises: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Title title="Gestion des devises" />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Devises
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gérez les devises supportées pour la facturation
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={loading}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Nouvelle devise
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <CurrencyExchangeIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  {currencies.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Devises totales
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h6" color="success.dark" fontWeight={700}>
                  {activeCurrencies}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  {activeCurrencies}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Devises actives
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {defaultCurrency && (
          <Card sx={{ minWidth: 200, bgcolor: 'primary.50' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <StarIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {defaultCurrency.code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Devise par défaut
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Le taux de change est calculé par rapport au XOF (Franc CFA).
          Par exemple, si 1 EUR = 655.957 XOF, le taux de change de l'EUR est 655.957.
        </Typography>
      </Alert>

      {/* Currency Table */}
      {currencies.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CurrencyExchangeIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Aucune devise configurée
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ajoutez des devises pour permettre la facturation multi-devises
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Ajouter une devise
            </Button>
          </CardContent>
        </Card>
      ) : (
        <CurrencyTable
          currencies={currencies}
          onEdit={handleEdit}
          onSetDefault={handleSetDefault}
        />
      )}

      {/* Create/Edit Dialog */}
      <CurrencyDialog
        open={dialogOpen}
        currency={selectedCurrency}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        loading={creating || updating || settingDefault}
      />
    </Box>
  );
}

export default CurrencyList;
